import { useQuery } from "@tanstack/react-query";
import {
  getProResearchStrategy,
  ProResearchGroup,
  ProResearchSummaryStatsDataModel,
  SearchMode,
} from "helpers/api";
import useSearchById from "hooks/useSearchById";
import { useEffect, useState } from "react";

// Interval at which to poll for updated Pro Research strategy
const POLL_INTERVAL_MS = 3000;

const PRO_RESEARCH_QUERY_KEY = "pro-research-strategy";

// Consensus Meter execution step title
// TODO(Aakash): Create a search step type enum for this in the future.
const CONSENSUS_METER_EXECUTION_STEP_TITLE = "Consensus Meter";

type UseProResearchReturn = {
  groups: ProResearchGroup[];
  isLoading: boolean;
  isError: boolean;
  refresh: () => void;
  currentExecutingStepTitle?: string;
  currentExecutingStepIndex?: number;
  totalExecutionSteps?: number;
  numSearches?: number;
  allGroupsComplete?: boolean;
  summaryStats?: ProResearchSummaryStatsDataModel;
  isComplete?: boolean;
};

/**
 * Hook to fetch and transform pro research strategy data for a thread interaction
 * @param threadInteractionId - The ID of the thread interaction
 * @returns The transformed strategy steps, loading state, error state, and refresh function
 */
export const useAgenticSearchStrategy = (
  threadInteractionId?: string,
  isProResearch: boolean = false,
  disabled: boolean = false
): UseProResearchReturn => {
  const [enablePolling, setEnablePolling] = useState(true);

  const query = useQuery(
    [PRO_RESEARCH_QUERY_KEY, threadInteractionId],
    async () => {
      if (!threadInteractionId) {
        return null;
      }
      return await getProResearchStrategy(threadInteractionId);
    },
    {
      refetchInterval: POLL_INTERVAL_MS,
      refetchIntervalInBackground: true,
      staleTime: 0,
      enabled: !!threadInteractionId && enablePolling && !disabled,
      retryDelay(failureCount, error) {
        return failureCount ** 2 * 500; // 1st attempt: 500ms, 2nd: 2000ms, 3rd: 4500ms, 4th: 8000ms, etc.
      },
      retry(failureCount, error) {
        return failureCount < 20; // Retry up to 20 times
      },
    }
  );

  let searchId = query.data?.search_id;
  const { isComplete } = useSearchById(
    searchId,
    !!searchId,
    isProResearch,
    SearchMode.PRO_RESEARCH
  );

  useEffect(() => {
    if (isComplete && enablePolling) {
      setEnablePolling(false);
    }
  }, [isComplete, enablePolling]);

  const groupsToRender =
    query.data?.strategy.groups.filter((group) => group.group_order >= 0) ?? [];

  const { title: currentExecutingStepTitle, index: currentExecutingStepIndex } =
    extractExecutingGroupMetadata(groupsToRender);

  // Calculate the number of searches discluding groups with group_order < 0
  // since negative group_order is used for non-rendered groups (like the consensus meter).
  const numSearches = groupsToRender.reduce(
    (acc, group) => acc + (group.searches?.length ?? 0),
    0
  );

  const allGroupsComplete = groupsToRender.every(
    (group) =>
      group.searches.length > 0 &&
      group.searches.every((search) => search.status === "COMPLETE")
  );

  return {
    groups: groupsToRender,
    isLoading: query.isLoading,
    isError: query.isError,
    refresh: query.refetch,
    currentExecutingStepTitle,
    currentExecutingStepIndex,
    totalExecutionSteps: groupsToRender.length,
    numSearches,
    allGroupsComplete,
    summaryStats: query.data?.summary_stats,
    isComplete,
  };
};

/**
 * Finds the title and index of the currently executing group.
 */
export function extractExecutingGroupMetadata(groups: ProResearchGroup[]): {
  title?: string;
  index?: number;
} {
  if (groups.length === 0) {
    return { title: undefined, index: undefined };
  }

  let firstIncompleteIndex = -1;
  let allGroupsComplete = true;

  // Single pass through groups to find executing group and check completion status
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    const hasSearches = group.searches.length > 0;
    const hasInProgress =
      hasSearches &&
      group.searches.some((search) => search.status === "IN_PROGRESS");
    const isComplete =
      hasSearches &&
      group.searches.every((search) => search.status === "COMPLETE");

    // If any group has IN_PROGRESS searches, that's the executing group
    if (hasInProgress) {
      return {
        title: group.label,
        index: i + 1, // 1-based index
      };
    }

    // Track the first incomplete group (empty searches or has non-complete searches)
    if (firstIncompleteIndex === -1 && !isComplete) {
      firstIncompleteIndex = i;
    }

    // If any group is not complete, mark allGroupsComplete as false
    if (!isComplete) {
      allGroupsComplete = false;
    }
  }

  // No groups have IN_PROGRESS searches
  if (allGroupsComplete) {
    // All groups are complete, return the last group
    const lastIndex = groups.length - 1;
    return {
      title: groups[lastIndex].label,
      index: lastIndex + 1,
    };
  }

  // Return the first incomplete group, or undefined if none found
  if (firstIncompleteIndex !== -1) {
    return {
      title: groups[firstIncompleteIndex].label,
      index: firstIncompleteIndex + 1,
    };
  }

  return { title: undefined, index: undefined };
}
